import { Box, InputLabel, OptionType, SelectMenu } from '@fullstory/components';
import { Classification } from '@fullstory/proto/fs/services/search-settings';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

interface ClassificationRuleSelectProps {
  elementId?: string;
  selectedClassification?: Classification | null;
  classificationOptions: OptionType<Classification>[];
  loadingClassification: boolean;
  onClassificationChange: (classification: Classification | null) => void;
  disabled?: boolean;
  preventOverflow?: boolean;
}

export const ClassificationRuleSelect: React.FC<ClassificationRuleSelectProps> = ({
  selectedClassification,
  classificationOptions,
  loadingClassification,
  onClassificationChange,
  disabled = false,
  preventOverflow = false,
}) => {
  const intl = useIntl();

  const selectedOption = useMemo(
    () => classificationOptions.find(option => option.value === selectedClassification) || null,
    [classificationOptions, selectedClassification],
  );

  const handleChange = useCallback(
    (option: OptionType<Classification> | null) => {
      onClassificationChange(option?.value ?? null);
    },
    [onClassificationChange],
  );

  return (
    <Box data-component="ClassificationRuleSelect" testId="classification-rule-select">
      <InputLabel htmlFor="classification-rule-select-menu" optional>
        <FormattedMessage defaultMessage="Data Classification" id="NJ1FSn" />
      </InputLabel>
      <SelectMenu
        id="classification-rule-select-menu"
        options={classificationOptions}
        selected={selectedOption}
        onChange={handleChange}
        disabled={disabled || loadingClassification}
        variant="fill-builder"
        clearable
        placeholder={
          loadingClassification
            ? intl.formatMessage({ defaultMessage: 'Loading...', id: 'gjBiyj' })
            : intl.formatMessage({ defaultMessage: 'Select classification...', id: 'JAnLRx' })
        }
        preventOverflow={preventOverflow ? 16 : undefined}
        flipY={preventOverflow || undefined}
        testId="classification-rule-select-menu"
      />
    </Box>
  );
};
